<template>
  <div>
    <v-data-table
      :headers="translatedHeaders"
      :items="items"
      :item-key="itemKey"
      :server-items-length="totalItems"
      :options.sync="options"
      :loading="loading"
      :hide-default-header="custom"
      :show-select="selectable"
      v-model="selectedItems"
      :footer-props="{
        itemsPerPageOptions
      }"
      :class="{'elevation-1' : !noElevation}"
      style="position: relative;"
      :disable-pagination="unpaged"
      :hide-default-footer="unpaged || hideDefaultFooter"
      v-sortable-table
      @dragstarted="onDragStarted"
      @dragsorted="onDragReordered"
    >
      <template v-slot:top v-if="!!$slots['filters'] || !!$slots['actions'] || exportable" class="pt-5 pb-3">
        <v-card v-if="!!$slots['filters']" class="elevation-0">
          <v-card-text class="pt-3">
            <v-spacer></v-spacer>
            <v-row class="justify-end align-center table-filters">
              <slot name="filters" />
            </v-row>
          </v-card-text>
        </v-card>
        <v-card
          v-if="exportable || !!$slots['actions']"
          class="elevation-0"
        >
          <v-card-text class="d-flex justify-end pt-3">
            <slot name="actions" />
            <div :title="maximumNumberOfExportableItemsReached ? $t('general.tooManyResultsToExport') : null">
              <v-btn
                v-if="exportable"
                @click="exportAll"
                :loading="exportLoading"
                :disabled="exportDisabled"
              >
                <v-icon left>mdi-file-export-outline</v-icon>
                {{ $t('general.doExport') }}
              </v-btn>
            </div>
          </v-card-text>
        </v-card>
      </template>

      <template v-slot:body="{ items }">
        <tbody v-if="errorMessage">
          <tr>
            <td colspan="99">
              <v-alert type="error" prominent class="ma-4">
                <v-row align="center">
                  <v-col class="grow">{{ errorMessage }}</v-col>
                  <v-col class="shrink">
                    <v-btn @click="loadDataWithoutResult">{{ $t('general.table.reload') }}</v-btn>
                  </v-col>
                </v-row>
              </v-alert>
            </td>
          </tr>
        </tbody>
        <slot v-else-if="!!$scopedSlots['body']" name="body" :items="items"/>
        <slot v-else-if="custom" name="custom" :items="items"/>
        <tbody v-else-if="items.length > 0">
          <slot name="body.prepend"></slot>
          <tr v-for="(item, index) in items"
              :key="item.id ? item.id : index"
              :class="{'item-selected': isItemSelected(item)}">
            <td v-if="selectable">
              <v-checkbox v-model="selectedItems" :value="item"></v-checkbox>
            </td>

            <template v-for="header in translatedHeaders">
              <td v-if="hasItemSlot(header.value)"
                  :key="header.value"
                  :class="'text-' + header.align" :style="getStyleByHeader(header)">
                <slot :name="'item.'+header.value" :item="item"/>
              </td>

              <td v-else-if="header.value === 'actions'" class="text-right"
                  :key="header.value"
                  style="white-space: nowrap;">
              </td>

              <td v-else :key="header.value" :style="getStyleByHeader(header)">
                <template v-if="typeof getItemValueByKey(item, header.value) == 'boolean'">
                  <boolean-flag-icon :value="getItemValueByKey(item, header.value)" />
                </template>
                <template v-else-if="isDateString(getItemValueByKey(item, header.value))">
                  {{ formatDate(getItemValueByKey(item, header.value), header.dateTimeFormat || 'L') }}
                </template>
                <template v-else-if="isDateTimeString(getItemValueByKey(item, header.value))">
                  <span
                    :title="formatDate(getItemValueByKey(item, header.value), 'LLL')"
                  >{{ formatDate(getItemValueByKey(item, header.value), header.dateTimeFormat) }}</span>
                </template>
                <template v-else-if="header.type === 'currency'">
                  <currency-amount :amount="getItemValueByKey(item, header.value)"></currency-amount>
                </template>
                <template v-else>{{ getItemValueByKey(item, header.value) }}</template>
              </td>
            </template>
          </tr>
          <slot name="body.append"></slot>
        </tbody>
        <tbody v-else>
          <tr>
            <td colspan="99">
              <div class="text-center text--disabled">
                {{ loading ? trans('general.table.loading') : trans('general.noData') }}
              </div>
            </td>
          </tr>
        </tbody>
      </template>
    </v-data-table>
    <div style="margin-top: -40px;" v-if="isDevelopment">
      <v-btn text color="grey" small @click="loadDataWithoutResult" title="Reload table"><v-icon small left>mdi-reload</v-icon>Reload</v-btn>
    </div>
  </div>
</template>

<script>
import {DateUtils} from '../utils/date-utils';
import CurrencyAmount from './CurrencyAmount';
import SortableTableDirective from '../plugins/sortable-table';
import {LocaleUtils} from '../utils/locale-utils';
import {MainUtils} from '@/utils/main-utils';
import {FilterUtils} from '../utils/filter-utils';
import {DevUtils} from '../utils/dev-utils';
import BooleanFlagIcon from './BooleanFlagIcon';

export default {
  name: 'AjaxTable',
  components: {BooleanFlagIcon, CurrencyAmount},
  mixins: [
    MainUtils,
    DateUtils,
    LocaleUtils,
    FilterUtils,
    DevUtils
  ],
  directives: {
    sortableTable: SortableTableDirective
  },
  props: {
    filter: null,
     /** @type {HeaderOption[]} */
    headers: null,
    resource: null,
    itemKey: {
      type: String,
      default: () => 'id'
    },
    /** List of hidden columns based on their 'id' (header.value) */
    hiddenColumns: {
      type: Array,
      default () {
        return [];
      }
    },
    itemsPerPageOptions: {
      type: Array,
      default() {
        return [10, 20, 50, 100];
      }
    },
    pageOptions: {
      type: Object,
      default: null
    },
    initSortBy: {
      type: [Object, String],
      default: null
    },
    unpaged: {
      type: Boolean,
      default: false
    },
    selectable: {
      type: Boolean,
      default: false
    },
    noElevation: {
      type: Boolean,
      default: false
    },
    params: {
      type: Object,
      default: null
    },
    exportable: {
      type: Boolean,
      default: false
    },
    exportOptions: {
      type: Object,
      default: null
    },
    hideDefaultFooter: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      translatedHeaders: [],

      totalItems: null,
      items: [],
      maximumExportableItems: null,
      options: {},
      loading: false,
      reloadAfterLoadData: false,
      errorMessage: "",
      selectedItems: [],
      showDeleteModal: false,
      performingDelete: false,
      exportLoading: false
    }
  },
  computed: {
    custom () {
      return !!this.$scopedSlots['custom'];
    },
    filterQueryString () {
      return this.buildFilterString(this.filter);
    },
    maximumNumberOfExportableItemsReached () {
      return this.totalItems !== null && this.totalItems > this.maximumExportableItems;
    },
    exportDisabled () {
      return this.maximumNumberOfExportableItemsReached;
    }
  },
  watch: {
    filterQueryString: {
      handler () {
        this.options.page = 1;
        if(this.loading) {
          this.reloadAfterLoadData = true;
        }
        this.loadData();
      },
    },
    params: {
      deep: true,
      handler () {
        this.loadData();
      },
    },
    items: {
      handler () {
        this.selectedItems = [];
      },
      deep: true
    },
    options: {
      handler (options) {
        this.loadData();
        this.$emit('update:page-options', options);
      },
      deep: true
    },
    selectedItems: {
      handler () {
        this.$emit('item-selection-changed', this.selectedItems);
      },
      deep: true
    },
    headers: {
      immediate: true,
      deep: true,
      handler () {
        this.translatedHeaders = [];

        for (let i = 0; i < this.headers.length; i++) {
          let header = this.headers[i];

          /**
           * @typedef {Object} HeaderOption
           * @property {string} text - Label of the column
           * @property {string} value - Field name that will be used to retrieve the item's content
           * @property {string} type - Custom defined data type, e.g. currency
           * @property {boolean} sortable - Is column sortable
           * @property {string} align - How content is aligned in the td
           * @property {string} width - Styling of the td
           * @property {string} maxWidth - Styling of the td
           * @property {string} dateTimeFormat - If item content is of date or datetime type, this will define how the value is formatted (default 'L' for date and 'LLL' for datetime)
           **/

          this.translatedHeaders.push({
            text: header.text ? this.transc(header.text, header.textCount || 1) : '',
            value: header.value,
            type: header.type,
            sortable: 'sortable' in header ? header.sortable : true,
            align: 'align' in header ? header.align : 'left',
            width: 'width' in header ? header.width : 'auto',
            maxWidth: 'maxWidth' in header ? header.maxWidth : 'auto',
            dateTimeFormat: 'dateTimeFormat' in header ? header.dateTimeFormat : null,
          });
        }

        if (this.hiddenColumns !== null && this.hiddenColumns.length > 0) {
          this.translatedHeaders = this.translatedHeaders.filter(header => !this.hiddenColumns.includes(header.value));
        }
      }
    }
  },
  created () {
    this.options = this.getPageOptions();
  },
  mounted () {
    if (this.resource === undefined || this.resource.length === 0) {
      this.errorMessage = this.trans('general.table.resource_error');
    }

    this.loadData();
  },
  methods: {
    loadDataWithoutResult() {
      this.loadData();
    },
    loadData () {
      let self = this;

      if(self.loading || !this.options.page) {
        return;
      }

      self.loading = true;
      self.errorMessage = '';

      return this.$http.get(
          self.resource,
          {
            params: this.getQueryParams()
          })
          .then(response => {
            self.items = response.data.content;
            self.totalItems = response.data.totalElements;
            self.maximumExportableItems = response.data.maximumExportableElements;

            self.$emit('items-loaded', self.items);
            self.$emit('page-loaded', response.data);
          })
          .catch(error => {
            console.error(error);
            self.errorMessage = this.getErrorMessageByResponse(error);
          })
          .finally(() => {
            self.$nextTick(() => {
              self.loading = false;
              if(self.reloadAfterLoadData) {
                self.reloadAfterLoadData = false;
                self.loadData();
              }
            });
          });
    },
    getQueryParams () {
      const { sortBy, sortDesc, page, itemsPerPage } = this.options;

      let queryParams = {};

      if (!this.unpaged) {
        queryParams.page = page - 1;
        queryParams.pageSize = itemsPerPage;
      }

      if (sortBy && sortBy.length > 0 && sortDesc.length > 0) {
        queryParams.sort = sortBy + (sortDesc[0] ? "+DESC" : "+ASC");
      }

      if (this.filterQueryString) {
        queryParams.filter = this.filterQueryString;
      }

      if (this.params !== null) {
        Object.assign(queryParams, this.params);
      }

      return queryParams;
    },
    hasItemSlot (slotName) {
      return !!this.$scopedSlots['item.'+slotName]
    },
    /**
     * Get result from item, nested keys possible (e.g. item.company.name)
     * @param {object} item
     * @param {string} key
     * @returns {string}
     */
    getItemValueByKey (item, key) {
      let keys;
      let value = item;

      if (!item || typeof key === 'undefined' || !key || key.length === 0) {
        return '';
      }

      keys = key.split('.');

      try {
        keys.forEach((oneKey) => {
          if (typeof value[oneKey] === 'undefined') {
            throw new Error(`No key ${oneKey} given`);
          }
          value = value[oneKey];
        });
      } catch (e) {
        console.warn(`[ajax-table] Could not retrieve key ${key} from item`, item, (e.message || e));
        return '';
      }

      return value;
    },
    isDateString (dateString) {
      return typeof dateString === 'string' && dateString.match(/[1,2]\d{3}-\d{1,2}(-\d{1,2})?$/);
    },
    isDateTimeString (dateString) {
      return typeof dateString === 'string' && dateString.match(/[1,2]\d{3}-\d{1,2}-\d{1,2}T\d{2}:\d{2}:\d{2}(\.\d+Z)?/);
    },
    isItemSelected (item) {
      let isItemSelected = false;

      this.selectedItems.forEach((selectedItem) => {
        if (selectedItem.id === item.id) {
          isItemSelected = true;
        }
      });

      return isItemSelected;
    },
    onDragReordered (event) {
      const movedItem = this.items.splice(event.oldIndex, 1)[0];
      this.items.splice(event.newIndex, 0, movedItem);
      this.$nextTick(() => {
        this.$emit('drag-reordered', {event: event, items: this.items});
      });
    },
    onDragStarted (event) {
      this.$nextTick(() => {
        this.$emit('drag-started', {event: event, items: this.items});
      });
    },
    exportAll () {
      this.exportLoading = true;

      this.$http.get(this.resource + this.getExportOptions().url,
        {
          params: this.getQueryParams(),
          timeout: 60 * 1000,
          responseType: 'arraybuffer'
        })
      .then(this.downloadResponseAsFile)
      .catch(this.handleErrorResponse)
      .finally(() => {
        this.$nextTick(() => {
          this.exportLoading = false;
        });
      });
    },
    getDefaultExportOptions () {
      return {
        url: '/export'
      }
    },
    getExportOptions () {
      return Object.assign({}, this.getDefaultExportOptions(), this.exportOptions !== null ? this.exportOptions : {});
    },
    getDefaultPageOptions () {
      const pageOptions = {
        page: 1,
        itemsPerPage: 20,
      };

      let sortOptions = {
        sortBy: [],
        sortDesc: []
      };

      if(this.initSortBy) {
        if(typeof this.initSortBy === 'string') {
          sortOptions.sortBy.push(this.initSortBy);
          sortOptions.sortDesc.push(false);
        } else if('name' in this.initSortBy) {
          sortOptions.sortBy.push(this.initSortBy.name);
          sortOptions.sortDesc.push(!(this.initSortBy.asc === true));
        }
      }

      return Object.assign({}, pageOptions, sortOptions);
    },
    getPageOptions () {
      return Object.assign({}, this.getDefaultPageOptions(), this.pageOptions !== null ? this.pageOptions : {});
    },

    getStyleByHeader (header) {
      let style = {};

      if (header.width) {
        style.width = header.width;
      }

      if (header.maxWidth) {
        style.maxWidth = header.maxWidth;
      }

      return style;
    }
  },
}
</script>

<style>
.item-selected td {
    background-color: rgba(55, 152, 162, 0.2);
}
.item-selected:hover {
    background-color: rgba(55, 55, 55, 0.2);
}

.table-filters > :not([class*='col-']) {
  margin: 0 8px;
}
</style>
